import {
    GripVertical,
    Lightbulb,
    Plus,
    Search,
    Sparkles,
    Trash2
} from 'lucide-react';
import { useEffect, useState } from 'react';

const StrengthsWeaknesses = ({
    // Données reçues du parent
    canOffer = [],
    seeking = [],
    loading = false,
    error = null,
    saving = false,
    
    // Handlers pour les modifications
    onStrengthsChange,
    onWeaknessesChange
}) => {
    // États locaux pour l'UI seulement
    const [newOffer, setNewOffer] = useState('');
    const [newSeek, setNewSeek] = useState('');
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedFrom, setDraggedFrom] = useState(null);
    const [showOfferSuggestions, setShowOfferSuggestions] = useState(false);
    const [showSeekSuggestions, setShowSeekSuggestions] = useState(false);

    // Suggestions unifiées et triées par ordre alphabétique
    const allSuggestions = [
        'Accompagnement juridique', 'Analyse financière', 'Audit qualité', 'Capital de départ',
        'Certification qualité', 'Coaching business', 'Coaching professionnel', 'Communication digitale',
        'Comptabilité', 'Conseil en management', 'Conseil stratégique', 'Compétences digitales',
        'Développement international', 'Développement web', 'Design graphique', 'Expertise fiscale',
        'Expertise technique', 'Financement bancaire', 'Financement participatif', 'Formation',
        'Formation commerciale', 'Formation leadership', 'Gestion de projet', 'Gestion RH',
        'Innovation produit', 'Juridique', 'Mentorat senior', 'Négociation immobilière',
        'Outils de gestion', 'Optimisation fiscale', 'Partenaires locaux', 'Photographie',
        'Rédaction de contenu', 'Réseau professionnel', 'Réseaux sociaux', 'Relations publiques',
        'Stratégie export', 'Stratégie marketing', 'Support commercial', 'Talents tech'
    ].sort();

    const handleAddOffer = () => {
        if (newOffer.trim() && !canOffer.includes(newOffer.trim())) {
            const newOffers = [...canOffer, newOffer.trim()];
            if (onStrengthsChange) {
                onStrengthsChange(newOffers);
            }
            setNewOffer('');
        }
    };

    const handleAddSeek = () => {
        if (newSeek.trim() && !seeking.includes(newSeek.trim())) {
            const newSeeks = [...seeking, newSeek.trim()];
            if (onWeaknessesChange) {
                onWeaknessesChange(newSeeks);
            }
            setNewSeek('');
        }
    };

    const handleRemoveOffer = (index) => {
        const newOffers = canOffer.filter((_, i) => i !== index);
        if (onStrengthsChange) {
            onStrengthsChange(newOffers);
        }
    };

    const handleRemoveSeek = (index) => {
        const newSeeks = seeking.filter((_, i) => i !== index);
        if (onWeaknessesChange) {
            onWeaknessesChange(newSeeks);
        }
    };

    const handleSuggestionClick = (suggestion, type) => {
        if (type === 'offer' && !canOffer.includes(suggestion)) {
            const newOffers = [...canOffer, suggestion];
            if (onStrengthsChange) {
                onStrengthsChange(newOffers);
            }
        } else if (type === 'seek' && !seeking.includes(suggestion)) {
            const newSeeks = [...seeking, suggestion];
            if (onWeaknessesChange) {
                onWeaknessesChange(newSeeks);
            }
        }
    };

    // Drag & Drop handlers
    const handleDragStart = (e, item, from, index) => {
        setDraggedItem({ item, index });
        setDraggedFrom(from);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, to) => {
        e.preventDefault();
        if (!draggedItem) return;

        const { item, index } = draggedItem;

        // Empêcher d'ajouter un doublon lors du déplacement
        if ((to === 'offer' && canOffer.includes(item)) || (to === 'seek' && seeking.includes(item))) {
            setDraggedItem(null);
            setDraggedFrom(null);
            return;
        }

        // Supprimer de la source et ajouter à la destination
        if (draggedFrom === 'offer') {
            const newOffers = canOffer.filter((_, i) => i !== index);
            if (onStrengthsChange) {
                onStrengthsChange(newOffers);
            }
        } else {
            const newSeeks = seeking.filter((_, i) => i !== index);
            if (onWeaknessesChange) {
                onWeaknessesChange(newSeeks);
            }
        }

        // Ajouter à la destination
        if (to === 'offer') {
            const newOffers = [...canOffer, item];
            if (onStrengthsChange) {
                onStrengthsChange(newOffers);
            }
        } else {
            const newSeeks = [...seeking, item];
            if (onWeaknessesChange) {
                onWeaknessesChange(newSeeks);
            }
        }

        setDraggedItem(null);
        setDraggedFrom(null);
    };

    const calculateMatches = () => {
        // Simulation du nombre de profils complémentaires
        return Math.min(canOffer.length * seeking.length * 2, 47);
    };

    // Affichage du loading
    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex flex-col shadow-sm">
            {/* Header */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-slate-700">
                        <h2 className="text-sm font-semibold">Complémentarité & Échanges</h2>
                    </div>
                    <div className="flex items-center space-x-2">
                        {saving && (
                            <span className="text-xs text-green-600 font-medium">
                                Sauvegarde...
                            </span>
                        )}
                        <div className="bg-gray-200 text-gray-700 px-2 py-1 rounded-lg text-xs font-medium">
                            {calculateMatches()} profils compatibles
                        </div>
                    </div>
                </div>
            </div>

            {/* Affichage d'erreur */}
            {error && (
                <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-red-500 mr-2 flex-shrink-0"></div>
                        <span className="text-sm text-red-800">{error}</span>
                    </div>
                </div>
            )}

            {/* Contenu principal en 2 colonnes */}
            <div className="flex-1 overflow-hidden flex">
                {/* Colonne gauche - Ce que je peux apporter */}
                <div className="flex-1 flex flex-col border-r border-gray-200 relative">
                    {/* Titre avec stats - position fixe */}
                    <div className="flex-shrink-0 px-4 pt-4 pb-2 bg-white border-b border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-slate-700 flex items-center">
                                <Lightbulb className="w-4 h-4 mr-2 text-emerald-500" />
                                Ce que je peux apporter
                            </h3>
                            <div className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium">
                                {canOffer.length}
                            </div>
                        </div>
                        {/* Zone d'ajout fixe en haut */}
                        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-2 relative">
                            <div className="flex gap-1">
                                <input
                                    type="text"
                                    value={newOffer}
                                    onChange={(e) => setNewOffer(e.target.value)}
                                    placeholder="Ajouter un atout..."
                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddOffer()}
                                />
                                <button
                                    onClick={handleAddOffer}
                                    disabled={!newOffer.trim()}
                                    className="px-2 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:bg-gray-300 transition-all duration-200"
                                >
                                    <Plus className="w-3 h-3" />
                                </button>
                                <button
                                    onClick={() => setShowOfferSuggestions(!showOfferSuggestions)}
                                    className={`px-2 py-1 rounded transition-all duration-200 ${showOfferSuggestions
                                        ? 'bg-emerald-500 text-white'
                                        : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                        }`}
                                    title="Voir les suggestions d'atouts"
                                >
                                    <Sparkles className="w-3 h-3" />
                                </button>
                            </div>

                            {/* Dropdown suggestions d'atouts */}
                            {showOfferSuggestions && (
                                <div className="absolute top-full left-2 right-2 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden">
                                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                                        <div className="flex items-center text-xs font-medium text-gray-700">
                                            <Lightbulb className="w-3 h-3 mr-1.5 text-emerald-600" />
                                            Suggestions d'atouts
                                        </div>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {allSuggestions.filter(s => !canOffer.includes(s)).map((suggestion, index) => (
                                            <button
                                                key={index}
                                                onClick={() => {
                                                    handleSuggestionClick(suggestion, 'offer');
                                                    setShowOfferSuggestions(false);
                                                }}
                                                className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-emerald-50 transition-colors border-b border-gray-50 last:border-b-0 flex items-center group"
                                            >
                                                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2.5 opacity-60 group-hover:opacity-100 transition-opacity"></div>
                                                <span className="flex-1">{suggestion}</span>
                                                <Plus className="w-3 h-3 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </button>
                                        ))}
                                        {allSuggestions.filter(s => !canOffer.includes(s)).length === 0 && (
                                            <div className="px-3 py-6 text-center text-xs text-gray-500">
                                                Toutes les suggestions ont été ajoutées
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Zone de contenu scrollable */}
                    <div className="flex-1 overflow-y-auto px-4 pb-4">
                        <div
                            className="space-y-2 pt-2"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, 'offer')}
                        >
                            {/* Cartes existantes */}
                            {canOffer.map((item, index) => (
                                <div
                                    key={index}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, item, 'offer', index)}
                                    className="group border border-gray-300 rounded-lg p-2 cursor-move hover:shadow-md hover:border-gray-400 transition-all duration-200"
                                    style={{
                                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 50%, #f0fdf4 100%)'
                                    }}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center flex-1">
                                            <GripVertical className="w-3 h-3 text-slate-400 mr-2" />
                                            <span className="text-xs text-slate-800 font-medium">{item}</span>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveOffer(index)}
                                            className="opacity-0 group-hover:opacity-100 w-4 h-4 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-all duration-200"
                                            title="Supprimer cet atout"
                                        >
                                            <Trash2 className="w-2.5 h-2.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {/* État vide compact */}
                            {canOffer.length === 0 && (
                                <div className="text-center py-8 text-slate-500">
                                    <Lightbulb className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                                    <p className="text-xs font-medium">Partagez vos atouts</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Colonne droite - Ce que je cherche */}
                <div className="flex-1 flex flex-col relative">
                    {/* Titre avec stats - position fixe */}
                    <div className="flex-shrink-0 px-4 pt-4 pb-2 bg-white border-b border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-slate-700 flex items-center">
                                <Search className="w-4 h-4 mr-2 text-orange-500" />
                                Ce que je cherche
                            </h3>
                            <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                                {seeking.length}
                            </div>
                        </div>
                        {/* Zone d'ajout fixe en haut */}
                        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-2 relative">
                            <div className="flex gap-1">
                                <input
                                    type="text"
                                    value={newSeek}
                                    onChange={(e) => setNewSeek(e.target.value)}
                                    placeholder="Ajouter un besoin..."
                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white"
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddSeek()}
                                />
                                <button
                                    onClick={handleAddSeek}
                                    disabled={!newSeek.trim()}
                                    className="px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-300 transition-all duration-200"
                                >
                                    <Plus className="w-3 h-3" />
                                </button>
                                <button
                                    onClick={() => setShowSeekSuggestions(!showSeekSuggestions)}
                                    className={`px-2 py-1 rounded transition-all duration-200 ${showSeekSuggestions
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                        }`}
                                    title="Voir les suggestions de besoins"
                                >
                                    <Sparkles className="w-3 h-3" />
                                </button>
                            </div>

                            {/* Dropdown suggestions de besoins */}
                            {showSeekSuggestions && (
                                <div className="absolute top-full left-2 right-2 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden">
                                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                                        <div className="flex items-center text-xs font-medium text-gray-700">
                                            <Search className="w-3 h-3 mr-1.5 text-orange-600" />
                                            Suggestions de besoins
                                        </div>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {allSuggestions.filter(s => !seeking.includes(s)).map((suggestion, index) => (
                                            <button
                                                key={index}
                                                onClick={() => {
                                                    handleSuggestionClick(suggestion, 'seek');
                                                    setShowSeekSuggestions(false);
                                                }}
                                                className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-orange-50 transition-colors border-b border-gray-50 last:border-b-0 flex items-center group"
                                            >
                                                <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2.5 opacity-60 group-hover:opacity-100 transition-opacity"></div>
                                                <span className="flex-1">{suggestion}</span>
                                                <Plus className="w-3 h-3 text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </button>
                                        ))}
                                        {allSuggestions.filter(s => !seeking.includes(s)).length === 0 && (
                                            <div className="px-3 py-6 text-center text-xs text-gray-500">
                                                Toutes les suggestions ont été ajoutées
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Zone de contenu scrollable */}
                    <div className="flex-1 overflow-y-auto px-4 pb-4">
                        <div
                            className="space-y-2 pt-2"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, 'seek')}
                        >
                            {/* Cartes existantes */}
                            {seeking.map((item, index) => (
                                <div
                                    key={index}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, item, 'seek', index)}
                                    className="group border border-gray-300 rounded-lg p-2 cursor-move hover:shadow-md hover:border-gray-400 transition-all duration-200"
                                    style={{
                                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 50%, #fff4e6 100%)'
                                    }}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center flex-1">
                                            <GripVertical className="w-3 h-3 text-slate-400 mr-2" />
                                            <span className="text-xs text-slate-800 font-medium">{item}</span>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveSeek(index)}
                                            className="opacity-0 group-hover:opacity-100 w-4 h-4 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-all duration-200"
                                            title="Supprimer ce besoin"
                                        >
                                            <Trash2 className="w-2.5 h-2.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {/* État vide compact */}
                            {seeking.length === 0 && (
                                <div className="text-center py-8 text-slate-500">
                                    <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                                    <p className="text-xs font-medium">Exprimez vos besoins</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StrengthsWeaknesses;